import { refreshToken as refreshTokenRequest } from "api/auth";
import type { AccountDto, AuthResponse } from "api/types";
import { getUtcTimestamp } from "utilities";

const refreshLeadTimeMs = 60 * 1000;

/** Name of the API's readable session-hint cookie (TTAPI-20). Holds "1" and nothing identifying. */
const SESSION_HINT_COOKIE = "tt_session";

export type StoredSession = {
	accessToken: string;
	expiresAt: string;
	user: AccountDto;
};

// The session lives only in memory. The long-lived refresh token stays in an httpOnly cookie the browser
// manages for us, so nothing auth-related is persisted to localStorage (and thus nothing is reachable by XSS).
let currentSession: StoredSession | null = null;
let hydrationPromise: Promise<StoredSession | null> | null = null;
let refreshPromise: Promise<StoredSession | null> | null = null;

export const readStoredSession = (): StoredSession | null => currentSession;

export const writeStoredSession = (session: StoredSession | null): void => {
	currentSession = session;
};

export const mapAuthToStoredSession = (auth: AuthResponse): StoredSession => ({
	accessToken: auth.accessToken,
	expiresAt: auth.expiresAt,
	user: auth.user
});

const shouldRefreshSession = (session: StoredSession): boolean =>
	getUtcTimestamp(session.expiresAt) - Date.now() <= refreshLeadTimeMs;

/**
 * Whether the API's non-httpOnly companion cookie says a session was issued. It carries no credential and
 * is never trusted for anything but skipping a request: a stale hint costs the one 401 we used to pay on
 * every load anyway, and the server clears it on the next refresh, so the two cannot stay out of step.
 */
const hasSessionHint = (): boolean =>
	typeof document !== "undefined" &&
	document.cookie.split(";").some((entry: string) => entry.trim().startsWith(`${SESSION_HINT_COOKIE}=`));

export const clearStoredSession = (): void => {
	writeStoredSession(null);
};

export const storeAuthSession = (auth: AuthResponse): StoredSession => {
	const session = mapAuthToStoredSession(auth);
	writeStoredSession(session);

	return session;
};

export const refreshStoredSession = async (): Promise<StoredSession | null> => {
	if (refreshPromise) {
		return refreshPromise;
	}

	// The refresh cookie carries the credential; a missing/expired cookie surfaces as a rejected request.
	refreshPromise = refreshTokenRequest()
		.then((auth: AuthResponse) => {
			const next = mapAuthToStoredSession(auth);
			writeStoredSession(next);

			return next;
		})
		.catch(() => {
			clearStoredSession();

			return null;
		})
		.finally(() => {
			refreshPromise = null;
		});

	return refreshPromise;
};

export const hydrateStoredSession = async (): Promise<StoredSession | null> => {
	if (hydrationPromise) {
		return hydrationPromise;
	}

	// No token survives a reload, so bootstrap needs a silent cookie refresh to find out whether anyone is
	// signed in. The refresh cookie is httpOnly and unreadable, so the API sets a companion hint cookie that
	// is not (TTAPI-20) — its absence means "definitely nobody", which lets a signed-out visitor skip the
	// round-trip entirely. Now that public trees render anonymously, that is most page loads and every crawl.
	if (!hasSessionHint()) {
		clearStoredSession();

		return null;
	}

	hydrationPromise = refreshStoredSession().finally(() => {
		hydrationPromise = null;
	});

	return hydrationPromise;
};

export const getValidStoredSession = async (): Promise<StoredSession | null> => {
	const current = readStoredSession();
	if (!current) {
		return null;
	}

	if (!shouldRefreshSession(current)) {
		return current;
	}

	return refreshStoredSession();
};

export const getRefreshLeadTimeMs = (): number => refreshLeadTimeMs;
